import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectLoggedInUserId } from '../../../../../Store/features/user/userSlice';
import axios from 'axios';
import { environment } from '../../../../../environments/environment';
import { toast } from 'react-hot-toast';
import Navbar from '../../../../Navbar/Navbar';
import Navbar3 from '../../../../Navbar/Navbar3/Navbar3';
import Footer from '../../../../Footer/Footer';
import QuestionsPopup from '../ServiceCards/QuestionsPopup';
import { FaShare } from "react-icons/fa6";
import SharePopup from "../../../../PopUp/SocialSharePopup/SharePopup";
import Login from "../../../../Login/Login";
import { getPriorityDMByIdAPI, getCommunityByIdAPI } from '../../../../../Service/user';
import './Pages.scss';
import { formatDistanceToNow } from 'date-fns';
import PurchasePopup from "../../../../Shared/PurchasePopup/PurchasePopup";
import { load } from "@cashfreepayments/cashfree-js";
import { createPaymentSession, verifyPayment, bookPriorityDMAPI } from "../../../../../Service/user";
import { joinCommunity } from "../../../../../Service/user";

const SkeletonLoader = () => (
  <div className="service-page">
    <Navbar />
    <div className="page-content">
      <div className="community-header">
        <div className="community-info">
          <div className="skeleton community-image" />
          <div className="community-details">
            <div className="skeleton title" />
            <div className="skeleton description" />
            <div className="skeleton description" />
          </div>
        </div>
      </div>

      <div className="priority-dm-content">
        <div className="content-header">
          <div className="skeleton title" />
          <div className="meta-info">
            <div className="skeleton badge" />
            <div className="skeleton badge" />
          </div>
        </div>

        <div className="description-section">
          <div className="skeleton title" />
          <div className="skeleton content-block" />
        </div>

        <div className="topics-section">
          <div className="skeleton title" />
          <div className="topics">
            <div className="skeleton topic" />
            <div className="skeleton topic" />
            <div className="skeleton topic" />
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

const PriorityDMPage = () => {
  const { communityId, dmId } = useParams();
  const navigate = useNavigate();
  const loggedInUserId = useSelector(selectLoggedInUserId);
  
  const [dm, setDm] = useState(null);
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [question, setQuestion] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [sharePopupOpen, setSharePopupOpen] = useState(false);
  const [userQuestions, setUserQuestions] = useState([]);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [allQuestions, setAllQuestions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAskLoading, setIsAskLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProduct, setIsProduct] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const getTimeLeft = (createdAt, timeline) => {
    const createdDate = new Date(createdAt);
    const timelineMinutes = timeline;
    const endDate = new Date(createdDate.getTime() + timelineMinutes * 60000);
    const now = new Date();
    
    if (now > endDate) {
      return "Time expired";
    }
    
    const minutesLeft = Math.floor((endDate - now) / 60000);
    return formatTimeline(minutesLeft);
  };

  useEffect(() => {
    fetchData();
  }, [communityId, dmId]);

  const fetchData = async () => {
    try {
      const [dmResponse, communityResponse] = await Promise.all([
        getPriorityDMByIdAPI(communityId, dmId),
        getCommunityByIdAPI(communityId)
      ]);
     
      setDm(dmResponse.data);
      setCommunity(communityResponse.data);

      // Check if user is admin
      const isUserAdmin = communityResponse.data.adminId._id === loggedInUserId;
      setIsAdmin(isUserAdmin);

      // Filter user's questions
      if (dmResponse.data.questions) {
        const userAskedQuestions = dmResponse.data.questions.filter(q => {
          const qUserId = q.userId._id || q.userId;
          return qUserId === loggedInUserId;
        });
        setUserQuestions(userAskedQuestions);
        setAllQuestions(dmResponse.data.questions);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeline = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else {
      const days = Math.floor(minutes / 1440);
      const remainingHours = Math.floor((minutes % 1440) / 60);
      if (remainingHours === 0) {
        return `${days} ${days === 1 ? 'day' : 'days'}`;
      }
      return `${days} ${days === 1 ? 'day' : 'days'} ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`;
    }
  };

  const handleJoinCommunity = async () => {
    setIsProduct(false);
    
    if (!community.isOpen) {
      toast.error("This community is closed for new members");
      return;
    }

    if (community.subscription === "paid") {
      setShowPaymentModal(true);
      return;
    }

    // Check if the user is already a member
    if (community.members.some((member) => member.member?._id === loggedInUserId)) {
      toast.error("You are already a member of this community");
      return;
    }
    
    try { 
      setIsJoining(true);
      await joinCommunity(communityId, { memberIds: [loggedInUserId] });
      toast.success('Successfully joined the community!');
      fetchData();
    } catch (error) {
      console.error('Error joining community:', error);
      toast.error('Failed to join community');
    } finally {
      setIsJoining(false);
    }
  };

  const isCommunityMember = useMemo(() => {
    return community?.members?.some(
      (member) => member.member._id === loggedInUserId
    );
  }, [community?.members, loggedInUserId]);

  // Initialize Cashfree SDK
  const initializeCashfree = async () => {
    try {
      return await load({ mode: "production" });
    } catch (error) {
      console.error("Failed to initialize Cashfree:", error);
      throw error;
    }
  };

  const handlePaymentSuccess = async (userDetails) => {
    try {
      const cashfree = await initializeCashfree();

      if (!isProduct) {
        const paymentResponse = await createPaymentSession({
          name: userDetails.name,
          email: userDetails.email,
          mobile: userDetails.mobile,
          amount: community.amount,
          entityId: community._id,
          entityType: "community",
        });

        const { order_id, payment_session_id } = paymentResponse.data;

        await cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal",
        });

        const verificationResponse = await verifyPayment({
          orderId: order_id,
          entityId: community._id,
          entityType: "community",
        });

        if (verificationResponse.data.status === "SUCCESS") {
          await joinCommunity(communityId, { memberIds: [loggedInUserId] });
          toast.dismiss();
          toast.success("Community joined successfully");
          fetchData();
        }
      }
      else{
        const paymentResponse = await createPaymentSession({
          name: userDetails.name,
          email: userDetails.email,
          mobile: userDetails.mobile,
          amount: dm.amount,
          entityId: dm._id,
          entityType: "priorityDM",
        });
  
        const { order_id, payment_session_id } = paymentResponse.data;
  
        await cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: "_modal",
        });
  
        const verificationResponse = await verifyPayment({
          orderId: order_id,
          entityId: dm._id,
          entityType: "priorityDM",
        });
  
        if (verificationResponse.data.status === "SUCCESS") {
          // Submit the question after successful payment
          await handleAskQuestion();
          toast.success("Payment successful and question submitted!");
        } else {
          toast.error("Payment verification failed");
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.dismiss();
      toast.error("Payment process failed. Please try again.");
    } finally {
      setShowPaymentModal(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    try {
      setIsAskLoading(true);
      const response = await bookPriorityDMAPI(communityId, dmId, { question });
      
      let newQuestion;
      if (!response.data) {
        newQuestion = {
          _id: Date.now().toString(),
          userId: {
            _id: loggedInUserId,
          },
          question: question,
          createdAt: new Date().toISOString(),
          isAnswered: false
        };
      } else {
        newQuestion = {
          ...response.data,
          userId: {
            _id: response.data.userId
          }
        };
      }

      // Update userQuestions state
      setUserQuestions(prev => [...prev, newQuestion]);
      
      setQuestion("");
      setShowQuestionForm(false);
      toast.success("Question submitted successfully!");
      
      // Refresh the data to get the updated questions
      fetchData();
    } catch (error) {
      console.error("Error submitting question:", error);
      toast.error("Failed to submit question");
    } finally {
      setIsAskLoading(false);
    }
  };

  const handleSubmitQuestion = async (e) => {
    e.preventDefault();
    
    // If the DM is paid and not free, show payment modal
    if (dm?.amount > 0) {
      setShowPaymentModal(true);
      return;
    }

    // For free questions, directly submit
    await handleAskQuestion();
  };

  const renderJoinButton = () => {
    if (!loggedInUserId) {
      return (
        <button 
          onClick={() => navigate('/login', { state: { from: window.location.pathname } })}
          className="join-button"
        >
          Login to Join Community
        </button>
      );
    }

    if (isAdmin) {
      return (
        <button 
          onClick={() => navigate(`/community/${communityId}`, { state: { activeTab: 'settings' } })}
          className="join-button manage-button"
        >
          Manage Community
        </button>
      );
    }

    if (isCommunityMember) {
      return (
        <button 
        onClick={() => navigate(`/community/${communityId}`)}
          className="join-button explore-button"
        >
          Explore Communities
        </button>
      );
    }

    return (
      <button 
        onClick={handleJoinCommunity} 
        disabled={isJoining}
        className="join-button"
      >
        {isJoining ? 'Joining...' : 'Join Community'}
      </button>
    );
  };

  const handleViewAllQuestions = () => {
    setShowAllQuestions(true);
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  return (
    <div className="service-page priority-dm-page">
      <Navbar />
      
      <div className="page-content">
        <div className="community-header">
          <div className="community-info">
            <img 
              src={community?.image || '/default-community-image.png'} 
              alt={community?.name}
              className="community-image"
            />
            <div className="community-details">
              <h2>{community?.name}</h2>
              <p><div dangerouslySetInnerHTML={{ __html: community?.about }} /></p>
              {renderJoinButton()}
            </div>
          </div>
        </div>

        <div className="priority-dm-content">
          <div className="content-header">
            <div className="section-title">PriorityDM</div>
            <h1>{dm?.title}</h1>
            <div className="meta-info">
              <span className={`price-badge ${dm?.amount === 0 ? "free" : "paid"}`}>
                {dm?.amount === 0 ? "Free" : `₹${dm?.amount}`}
              </span>
              <span className="response-time">
                <i className="far fa-clock"></i>
                Response Time: {formatTimeline(dm?.timeline)}
              </span>
            </div>
          </div>

          <button 
            className="share-button"
            onClick={() => setSharePopupOpen(true)}
          >
            <FaShare /> Share
          </button>

          <div className="description-section">
            <div className="section-title">Description</div>
            <div className="content">
              <div dangerouslySetInnerHTML={{ __html: dm?.description }} />
            </div>
          </div>

          {dm?.topics && (
            <div className="topics-section">
              <div className="section-title">Relevant Topics for this PriorityDM</div>
              <div className="topics">
                {dm.topics.map((topic, index) => (
                  <span key={index} className="topic-tag">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="questions-section">
            {isAdmin ? (
              <div className="admin-actions">
                <button 
                  className="view-questions-button"
                  onClick={handleViewAllQuestions}
                >
                  View All Questions
                  {allQuestions.filter(q => !q.isAnswered).length > 0 && (
                    <span className="question-badge">
                      {allQuestions.filter(q => !q.isAnswered).length}
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <div className="action-section">
                {!loggedInUserId ? (
                  <button onClick={() => navigate('/login', { state: { from: window.location.pathname } })}>
                    Login to Ask Questions
                  </button>
                ) : !isCommunityMember ? (
                  <button onClick={handleJoinCommunity} disabled={isJoining}>
                    {isJoining ? 'Joining...' : 'Join Community to Ask Questions'}
                  </button>
                ) : (
                  <>
                    <button onClick={() => setShowQuestionForm(!showQuestionForm)}>
                      Ask Question
                    </button>
                    {showQuestionForm && (
                      <form onSubmit={handleSubmitQuestion} className="question-form">
                        <textarea
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          placeholder="Type your question here..."
                          disabled={isAskLoading}
                        />
                        <div className="form-actions">
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={() => setShowQuestionForm(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="submit-button"
                            disabled={isAskLoading || !question.trim()}
                          >
                            {isAskLoading ? "Submitting..." : "Submit"}
                          </button>
                        </div>
                      </form>
                    )}
                    {userQuestions.length > 0 && (
                      <div className="user-questions-section">
                        <h4>Your Questions</h4>
                        {userQuestions.map((q, index) => (
                          <div key={index} className="question-item">
                            <div className="question-header">
                              <p className="question-text"><strong>Question:</strong> {q.question}</p>
                              <span className={`status-badge ${q.isAnswered ? 'answered' : 'pending'}`}>
                                {q.isAnswered ? 'Answered' : `Time left: ${getTimeLeft(q.createdAt, dm.timeline)}`}
                              </span>
                            </div>
                            {q.isAnswered && (
                              <p className="answer-text">
                                <strong>Answer:</strong> {q.answer}
                              </p>
                            )}
                            <small className="timestamp">
                              Asked {formatDistanceToNow(new Date(q.createdAt))} ago
                            </small>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showAllQuestions && (
        <QuestionsPopup
          questions={allQuestions}
          timeline={dm?.timeline}
          onClose={() => setShowAllQuestions(false)}
          communityId={communityId}
          dmId={dmId}
        />
      )}

      <SharePopup
        isOpen={sharePopupOpen}
        setIsOpen={setSharePopupOpen}
        url={window.location.href}
      />

      {showPaymentModal && (
        isProduct ? (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "priorityDM",
            name: dm?.title,
            description: dm?.description,
            amount: dm?.amount,
            is_free: dm?.amount === 0,
          }}
          loggedInUser={loggedInUser} 
          onProceed={handlePaymentSuccess}
        />
      ) : (
        <PurchasePopup
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          itemDetails={{
            type: "community",
            name: community?.name,
            memberCount: community?.members.length,
            isOpen: community?.isOpen,
            description: community?.about,
            amount: community?.amount,
            is_free: community?.subscription === "free",
          }}
          loggedInUser={loggedInUser} 
          onProceed={handlePaymentSuccess}
        />
      ))}

      <Footer />
    </div>
  );
};

export default PriorityDMPage; 